/* leafKey — THE ONE resolver for "which statement leaf does this gl_line_mappings row own?"
 *
 * ★ WHY A COLUMN AND NOT A SLUG (the whole point of the rename fix).
 * A balance-sheet leaf's key used to be `normalizeBSKey(display_name || gl_name)` and a
 * revenue leaf's `rev_${normalizeBSKey(gl_name)}` — recomputed at every import and stored
 * NOWHERE. So renaming a line minted a NEW key and stranded everything joined to the old
 * one: bs_assumptions.line_id and .base_is_line_id, fixed_assets.bs_line_id and
 * intangible_assets.bs_line_id (both NOT NULL), revenue_module_mappings.gl_line_key /
 * .bs_line_key, misc_revenue_lines.target_gl_line_key, companies.sweep_interest_is_line_key.
 * `gl_line_mappings.leaf_key` (added and backfilled 0-cell in Build 1, `b710c10`) pins that
 * identity, so a rename is now a LABEL-ONLY edit and is harmless BY CONSTRUCTION rather
 * than by a migration that has to be right every time.
 *
 * ★★ THE PRECEDENCE IS A PRECEDENCE, NEVER A MERGE — and that is a measured property,
 * not a hope. `is_line_key` is IS-only (0 BS rows carry one on any tenant) and is
 * deliberately NULL on the revenue rows, which is exactly where `leaf_key` was seeded.
 * Build 1 asserted the two are DISJOINT inside its own transaction ("0 rows carry both")
 * and its verifier re-asserted it across all 415 glm rows on four tenants. So the two
 * columns can never disagree about one row, and this function never has to choose
 * between two live answers.
 *
 * ⛔ SPELLED ONCE. Every consumer imports this. A second copy of the precedence is the
 * §6 divergence this whole workstream exists to remove — and it would be the quiet kind,
 * because a consumer that resolved a leaf differently would simply read $0 for it and
 * the statement would still add up.
 *
 * ⓘ EMPTY STRING COUNTS AS ABSENT. Plain `??` would return `''` for a row whose
 * is_line_key is empty rather than null, and an empty leaf id silently addresses nothing.
 * Neither column holds `''` on any tenant today; this is a guard against a shape that has
 * never occurred, not a workaround for one that has.
 */

const present = v => (v === null || v === undefined || v === '' ? null : v);

/* The pinned leaf id for a gl_line_mappings row, or null when the row owns no leaf
 * (Skip'd, payroll-handled, or a line added since the Build-1 backfill and not yet
 * pinned — see THE DERIVE FALLBACK note at each call site). */
export function leafKeyFor(row) {
    if (!row) return null;
    return present(row.is_line_key) ?? present(row.leaf_key);
}

/* True when the row owns a statement leaf at all — i.e. it is an operator GL line rather
 * than a synthetic (oi_interest, totals, headers), a rollup (total_clearing) or a
 * department-derived payroll leaf. This is the RENAMABLE predicate: a label edit is
 * meaningful exactly for the rows this returns true for. */
export function ownsLeaf(row) {
    return leafKeyFor(row) !== null;
}
